import { Route } from "react-router";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { AddIncome } from "@lending/pages/Common/AddIncome";
import { Address } from "@lending/pages/Common/Address";
import { ApplicationQuestions } from "@lending/pages/Common/ApplicationQuestions";
import { Disclosures } from "@lending/pages/Common/Disclosures";
import { EligibilitySelection } from "@lending/pages/Common/EligibilitySelection";
import { EmploymentDetails } from "@lending/pages/Common/EmploymentDetails";
import { LivingArrangement } from "@lending/pages/Common/LivingArrangement";
import { PersonalInfo } from "@lending/pages/Common/PersonalInfo";
import { PlaidPiiReview } from "@lending/pages/Common/PlaidPrefill/PersonalInfoReview";
import { PlaidPhoneNumber } from "@lending/pages/Common/PlaidPrefill/PlaidPhoneNumber";
import { Submitted } from "@lending/pages/Common/Submitted";
import { ConfirmInfo } from "@lending/pages/PersonalLoan/ConfirmInfo";
import { CreditPull } from "@lending/pages/PersonalLoan/CreditPull";
import { GetStarted } from "@lending/pages/PersonalLoan/GetStarted";
import { Offers } from "@lending/pages/PersonalLoan/Offers";
import { RequestedAmount } from "@lending/pages/PersonalLoan/RequestedAmount";
import { loadPrequalApp } from "@lending/utils/loadPrequalApp";
import { getCoApplicantRoutes } from "./coApplicantRoutes";
import { ApplicationLayout } from "./layouts/ApplicationLayout";
import { getPersonalLoanCanonicalRoutePolicy } from "./layouts/personalLoanRoutePolicy";
import { MainLayout } from "./MainLayout/MainLayout";
import { getMembershipShareRoutes } from "./membershipShareRoutes";
import { ProgressBarLayout } from "./ProgressBarLayout/ProgressBarLayout";

const webPersonalLoanRoutes = (
  <>
    <Route path="eligibility" element={<EligibilitySelection />} />
    <Route path="questions" element={<ApplicationQuestions />} />
    <Route path="get-started" element={<GetStarted />} />
    <Route
      path=":applicationUuid"
      element={
        <ApplicationLayout
          fallbackPath="/error"
          productType="personal-loan"
          useApplicationQuery={useGetPersonalLoanApplicationQuery}
          getRoutePolicy={getPersonalLoanCanonicalRoutePolicy}
        />
      }
    >
      <Route index element={null} />
      <Route path="eligibility" element={<EligibilitySelection />} />
      <Route path="questions" element={<ApplicationQuestions />} />
      <Route path="get-started" element={<GetStarted />} />
    </Route>
    <Route element={<MainLayout flowType="web" />}>
      <Route element={<ProgressBarLayout />}>
        <Route path="phone" element={<PlaidPhoneNumber />} />
        <Route path="personal-info" element={<PersonalInfo />} />
        <Route path="personal-info-review" element={<PlaidPiiReview />} />
        <Route
          path=":applicationUuid"
          element={
            <ApplicationLayout
              fallbackPath="/product-selection"
              productType="personal-loan"
              useApplicationQuery={useGetPersonalLoanApplicationQuery}
              getRoutePolicy={getPersonalLoanCanonicalRoutePolicy}
            />
          }
        >
          <Route index element={null} />
          <Route path="phone" element={<PlaidPhoneNumber />} />
          <Route path="personal-info-review" element={<PlaidPiiReview />} />
          <Route path="personal-info" element={<PersonalInfo />} />
          <Route path="address" element={<Address />} />
          <Route path="requested-amount" element={<RequestedAmount />} />
          <Route path="living-arrangement" element={<LivingArrangement />} />
          <Route path="add-income" element={<AddIncome />} />
          <Route path="employment-details" element={<EmploymentDetails />} />
          {getCoApplicantRoutes({
            productType: "personal_loans",
            fallbackPath: "/personal-loan/:applicationUuid/disclosures",
          })}
          <Route path="disclosures" element={<Disclosures />} />
        </Route>
      </Route>
      <Route
        path=":applicationUuid"
        element={
          <ApplicationLayout
            fallbackPath="/product-selection"
            productType="personal-loan"
            useApplicationQuery={useGetPersonalLoanApplicationQuery}
            getRoutePolicy={getPersonalLoanCanonicalRoutePolicy}
          />
        }
      >
        <Route index element={null} />
        <Route path="credit-pull" element={<CreditPull />} />
        <Route path="offers" element={<Offers />} />
        {getMembershipShareRoutes({ fallbackPath: "/personal-loan/:applicationUuid/submitted" })}
        <Route path="submitted" element={<Submitted />} />
      </Route>
    </Route>
  </>
);

const olbPersonalLoanRoutes = (
  <>
    <Route path="confirm-info" loader={loadPrequalApp} />
    <Route
      path=":applicationUuid"
      element={
        <ApplicationLayout
          fallbackPath="/olb-personal-loan/confirm-info"
          productType="personal-loan"
          useApplicationQuery={useGetPersonalLoanApplicationQuery}
          getRoutePolicy={getPersonalLoanCanonicalRoutePolicy}
        />
      }
    >
      <Route index element={null} />
      <Route element={<MainLayout flowType="olb" />}>
        {/* Routes with progress bar */}
        <Route element={<ProgressBarLayout />}>
          <Route path="confirm-info" element={<ConfirmInfo />} />
          <Route path="personal-info" element={<PersonalInfo />} />
          <Route path="requested-amount" element={<RequestedAmount />} />
          <Route path="address" element={<Address />} />
          <Route path="living-arrangement" element={<LivingArrangement />} />
          <Route path="add-income" element={<AddIncome />} />
          <Route path="employment-details" element={<EmploymentDetails />} />
          {getCoApplicantRoutes({
            productType: "personal_loans",
            fallbackPath: "/olb-personal-loan/:applicationUuid/disclosures",
          })}
          <Route path="disclosures" element={<Disclosures />} />
        </Route>
        {/* Routes without progress bar */}
        <Route path="credit-pull" element={<CreditPull />} />
        <Route path="offers" element={<Offers />} />
        <Route path="submitted" element={<Submitted />} />
      </Route>
    </Route>
  </>
);

export { webPersonalLoanRoutes, olbPersonalLoanRoutes };
